import { FeatureFlagName, type OnboardingSetupStage } from "~/gql/graphqlBase.generated";
import onboardingWelcome, { type JourneyProgressSummary, type WelcomeAudience } from "~/helpers/onboardingWelcome";
import useFeatureFlag from "~/hooks/useFeatureFlag";
import useOnboardingStanding from "~/hooks/useOnboardingStanding";
import { useGetMyOnboardingPlanQuery } from "~/services/endpoints/apiEndpoints";

export interface FernieWelcome {
	audience: WelcomeAudience;
	onboardingSetup: OnboardingSetupStage;
	progress: JourneyProgressSummary | null;
}

const useFernieWelcome = (): FernieWelcome | null => {
	const { isEnabled: isAiChatEnabled } = useFeatureFlag(FeatureFlagName.AiChat);
	const { standing } = useOnboardingStanding({ skip: !isAiChatEnabled });
	const {
		data: plan,
		isLoading: isPlanLoading,
		isUninitialized: isPlanUninitialized
	} = useGetMyOnboardingPlanQuery(undefined, { skip: !isAiChatEnabled });

	if (!standing || (isAiChatEnabled && (isPlanLoading || isPlanUninitialized))) return null;
	const { onboardingSetup, trainingComplete, welcome } = standing;
	const audience = onboardingWelcome.resolveShownWelcome({
		audience: onboardingWelcome.deriveAudience({
			dataIsIn: onboardingWelcome.isDataIn(plan?.myOnboardingPlan.upload.families),
			journeyExists: welcome.onboardingJourneyLookup.exists,
			journeyHasOpenWork: onboardingWelcome.hasOpenJourneyWork(welcome.onboardingJourneyState),
			journeyUnfinished: welcome.onboardingJourneyLookup.unfinished,
			onboardingSetup,
			trainingComplete
		}),
		isAiChatEnabled,
		now: Date.now(),
		onboardingSetup,
		preference: welcome.myOnboardingWelcomePreference
	});
	if (audience === null) return null;

	const journeyState = audience === "continue" ? welcome.onboardingJourneyState : null;
	return {
		audience,
		onboardingSetup,
		progress: journeyState ? onboardingWelcome.summarizeJourneyProgress(journeyState) : null
	};
};

export default useFernieWelcome;
